/**
 * EmbedLessonPage — встраиваемая версия урока
 * apps/web/src/pages/embed/EmbedLessonPage.tsx
 *
 * Поддержка:
 * - YouTube/RuTube видео (автоопределение по ссылке)
 * - Адаптивные картинки
 * - Таблицы с горизонтальным скроллом
 * - Подсветка кода
 */

import { useEffect, useState, useRef } from 'react';
import { lessonMarkdownComponents } from '../../lib/lesson-markdown';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getLesson,
  markLessonViewed,
  submitLessonFeedback,
  markCollectionLessonViewed,
  Lesson,
} from '../../lib/lessons-api';







export default function EmbedLessonPage() {
  const { id: collectionId, moduleId } = useParams<{ id: string; moduleId: string }>();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get('lessonId');
  const navigate = useNavigate();

  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [showKeyPoints, setShowKeyPoints] = useState(true);

  const startTimeRef = useRef<number>(Date.now());
  const hasMarkedViewedRef = useRef(false);

  useEffect(() => {
    if (!lessonId) return;
    const loadLesson = async () => {
      try {
        setLoading(true);
        const response = await getLesson(lessonId);
        setLesson(response.lesson);
        startTimeRef.current = Date.now();
        hasMarkedViewedRef.current = false;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    };
    loadLesson();
  }, [lessonId]);

  useEffect(() => {
    return () => {
      if (!lessonId || hasMarkedViewedRef.current) return;
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      if (timeSpent < 5) return;
      hasMarkedViewedRef.current = true;
      markLessonViewed(lessonId, timeSpent).catch(() => {});
      if (collectionId && moduleId) {
        markCollectionLessonViewed(collectionId, moduleId).catch(() => {});
      }
    };
  }, [lessonId, collectionId, moduleId]);

  const handleFeedback = async (type: 'positive' | 'negative') => {
    if (!lessonId || feedbackSent) return;
    try {
      await submitLessonFeedback(lessonId, type);
      setFeedbackSent(true);
    } catch {}
  };

  const handleStartTest = async () => {
    if (!collectionId || !moduleId || !lessonId) return;
    if (!hasMarkedViewedRef.current) {
      hasMarkedViewedRef.current = true;
      const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
      try { await markLessonViewed(lessonId, timeSpent); } catch {}
      try { await markCollectionLessonViewed(collectionId, moduleId); } catch {}
    }
    // Embed-навигация
    navigate(`/embed/collection/${collectionId}/play/${moduleId}`);
  };

  // Embed-навигация: назад к курсу
  const handleBack = () => {
    if (collectionId) {
      navigate(`/embed/collection/${collectionId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Загрузка урока...</p>
        </div>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">📚</div>
          <h1 className="text-xl font-bold mb-2">Урок не найден</h1>
          <p className="text-gray-400 mb-4">{error}</p>
          <button onClick={handleBack} className="px-6 py-2 bg-blue-600 rounded-lg">
            Назад к курсу
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="sticky top-0 bg-gray-900/95 backdrop-blur border-b border-gray-800 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-400 hover:text-white transition">
            <span>←</span>
            <span className="hidden sm:inline">Назад к курсу</span>
          </button>
          <h1 className="text-lg font-semibold truncate max-w-[200px] sm:max-w-none">{lesson.title}</h1>
          <div className="w-20" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 overflow-x-hidden">
        <article className="[--lesson-link:#60a5fa] [--lesson-bg:#1f2937] [--lesson-border:transparent] [--lesson-code-border:0px] [--lesson-table-border:#374151] [--lesson-muted:#9ca3af] prose prose-invert prose-lg max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={lessonMarkdownComponents()}
          >
            {lesson.content.text}
          </ReactMarkdown>
        </article>

        {/* Key Points */}
        {lesson.settings.showKeyPoints && (lesson.content.keyPoints?.length ?? 0) > 0 && (
          <div className="mt-8 bg-gray-800/50 border border-gray-700 rounded-xl p-4">
            <button onClick={() => setShowKeyPoints(!showKeyPoints)} className="flex items-center justify-between w-full text-left">
              <span className="font-semibold flex items-center gap-2">📌 Ключевые тезисы</span>
              <span className="text-gray-400">{showKeyPoints ? '▲' : '▼'}</span>
            </button>
            {showKeyPoints && (
              <ul className="mt-3 space-y-2">
                {lesson.content.keyPoints?.map((point: string, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-gray-300">
                    <span className="text-blue-400 mt-1">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Feedback */}
        <div className="mt-8 pt-6 border-t border-gray-800">
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-sm">Этот урок был полезен?</span>
            <div className="flex gap-2">
              <button onClick={() => handleFeedback('positive')} disabled={feedbackSent}
                className={`px-4 py-2 rounded-lg transition ${feedbackSent ? 'bg-gray-700 text-gray-500' : 'bg-gray-800 hover:bg-green-600/20 hover:text-green-400'}`}>
                👍 Да
              </button>
              <button onClick={() => handleFeedback('negative')} disabled={feedbackSent}
                className={`px-4 py-2 rounded-lg transition ${feedbackSent ? 'bg-gray-700 text-gray-500' : 'bg-gray-800 hover:bg-red-600/20 hover:text-red-400'}`}>
                👎 Нет
              </button>
            </div>
          </div>
          {feedbackSent && <p className="text-green-400 text-sm mt-2">Спасибо за отзыв!</p>}
        </div>

        {/* Test button */}
        {collectionId && moduleId && (
          <div className="mt-8">
            <button onClick={handleStartTest}
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-purple-700 transition flex items-center justify-center gap-2">
              📋 Пройти тест <span>→</span>
            </button>
          </div>
        )}
      </main>
    </div>
  );
}